import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  MapPin, Edit, Trash2, Phone, Mail, Bell, Building2, DollarSign, ChevronRight,
} from 'lucide-react';
import { DAYS_OF_WEEK } from './constants';

export default function LocationDetail({
  location, pricingRules, onEdit, onDelete, onGoPricing,
}) {
  if (!location) {
    return (
      <Card className="p-8 text-center">
        <p className="text-muted-foreground">Select a location to view details</p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-bold">{location.name}</h2>
          {!location.active && (
            <span className="text-xs bg-destructive/10 text-destructive px-2 py-0.5 rounded-full">
              Inactive
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => onEdit(location)} data-testid="edit-location-btn">
            <Edit className="w-4 h-4 mr-2" /> Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-destructive hover:bg-destructive/10"
            onClick={() => onDelete(location.id)}
            data-testid="delete-location-btn"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h3 className="font-bold text-sm uppercase text-muted-foreground">Contact Information</h3>

          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
            <div>
              <p>{location.address}</p>
              {location.city && (
                <p className="text-muted-foreground">
                  {location.city}{location.postal_code && `, ${location.postal_code}`}
                </p>
              )}
            </div>
          </div>

          {location.phone && (
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-muted-foreground" />
              <p>{location.phone}</p>
            </div>
          )}
          {location.email && (
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-muted-foreground" />
              <p>{location.email}</p>
            </div>
          )}
          {location.notification_email && (
            <div className="flex items-start gap-3 bg-blue-50 p-3 rounded-lg border border-blue-200 mt-2">
              <Bell className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-blue-900">Staff Notifications</p>
                <div
                  className="flex flex-wrap gap-1.5 mt-1"
                  data-testid="staff-notification-emails"
                >
                  {location.notification_email.split(',')
                    .map((e) => e.trim())
                    .filter(Boolean)
                    .map((email) => (
                      <span
                        key={email}
                        className="inline-block bg-white border border-blue-300 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full"
                      >
                        {email}
                      </span>
                    ))}
                </div>
                <p className="text-xs text-blue-600 mt-1">Receives pending allocation alerts</p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Building2 className="w-5 h-5 text-muted-foreground" />
            <p>{location.courts_count} Courts</p>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-sm uppercase text-muted-foreground">Operating Hours</h3>
          <div className="space-y-2">
            {DAYS_OF_WEEK.map((day) => {
              const hours = location.operating_hours?.[day];
              return (
                <div key={day} className="flex justify-between text-sm">
                  <span className="capitalize">{day}</span>
                  <span className={hours?.closed ? 'text-destructive' : ''}>
                    {hours?.closed ? 'Closed' : `${hours?.open || '08:00'} - ${hours?.close || '22:00'}`}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-6 pt-6 border-t space-y-4">
        <div>
          <h3 className="font-bold text-sm uppercase text-muted-foreground mb-3">Sports</h3>
          <div className="flex gap-2">
            {(location.sports || ['badminton', 'pickleball']).map((sport) => (
              <span key={sport} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm capitalize">
                {sport}
              </span>
            ))}
          </div>
        </div>

        {location.amenities && location.amenities.length > 0 && (
          <div>
            <h3 className="font-bold text-sm uppercase text-muted-foreground mb-3">Amenities</h3>
            <div className="flex flex-wrap gap-2">
              {location.amenities.map((amenity) => (
                <span key={amenity} className="px-3 py-1 bg-muted rounded-full text-sm">
                  {amenity}
                </span>
              ))}
            </div>
          </div>
        )}

        {location.description && (
          <div>
            <h3 className="font-bold text-sm uppercase text-muted-foreground mb-3">Description</h3>
            <p className="text-muted-foreground">{location.description}</p>
          </div>
        )}
      </div>

      <div className="mt-6 pt-6 border-t">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-sm uppercase text-muted-foreground flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Location Pricing Rules
          </h3>
          <Button variant="outline" size="sm" onClick={onGoPricing}>
            Manage Pricing
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {pricingRules.length === 0 ? (
          <p className="text-sm text-muted-foreground">No pricing rules configured for this location.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {pricingRules.slice(0, 4).map((rule) => (
              <div key={rule.id} className="p-3 bg-muted/30 rounded-xl text-sm">
                <div className="font-bold">${rule.price_per_hour}/hr</div>
                <div className="text-muted-foreground capitalize">
                  {rule.is_default ? 'Default' : rule.day_type} • {rule.sport}
                </div>
              </div>
            ))}
            {pricingRules.length > 4 && (
              <div className="p-3 bg-muted/30 rounded-xl text-sm flex items-center justify-center text-muted-foreground">
                +{pricingRules.length - 4} more rules
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
